import { useParams, useLocation } from 'react-router-dom';
import { VersionHistoryPanel } from '@/components/blueprint-version/VersionHistoryPanel';
import { GlossaryVersionHistoryPanel } from '@/components/object-version/ObjectVersionHistoryPanel';
import { useGlossaryBlueprintsDataContext } from '@/providers/GlossaryBlueprintsDataProvider';
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
export default function StructureSidebarHistory() {
  const { t } = usePlatformTranslation();
  const params = useParams();
  const location = useLocation();
  
  // Determine if we're viewing a template or a library/glossary item
  const isTemplateRoute = location.pathname.includes('/templates/');
  const isLibraryRoute = location.pathname.includes('/library/');
  
  // Support both 'itemId' (from /templates/:itemId) and 'blueprintId' routes
  const blueprintId = params.itemId || params.blueprintId;
  const glossaryItemId = params.itemId;
  
  const { loadData: loadBlueprintData, glossaryBlueprints } = useGlossaryBlueprintsDataContext();
  const { loadData: loadGlossaryData, glossaryItems } = useGlossaryDataContext();

  // For Templates/Blueprints
  if (isTemplateRoute) {
    // Find the current blueprint to get its version
    const currentBlueprint = glossaryBlueprints.find(bp => bp.id === blueprintId);
    
    // Extract current version (handle both old and new version formats)
    let currentVersion: { major: number; minor: number; patch: number } | undefined;
    if (currentBlueprint?.version) {
      if ('major' in currentBlueprint.version) {
        // New format: { major, minor, patch }
        currentVersion = {
          major: currentBlueprint.version.major,
          minor: currentBlueprint.version.minor,
          patch: currentBlueprint.version.patch,
        };
      } else if ('version' in currentBlueprint.version) {
        // Old format: { version: 0.00001, changes: {} }
        // Convert to major.minor.patch (not precise but good enough for comparison)
        currentVersion = { major: 0, minor: 0, patch: Math.round(currentBlueprint.version.version * 100000) };
      }
    }

    const handleRestore = async () => {
      // Refresh the blueprint data after restore
      await loadBlueprintData();
    };

    if (!blueprintId) {
      return (
        <div className="p-4 text-center text-muted-foreground">
          <p>{t('platform.layout.structure-menu.select_a_template_to_view_version_history')}</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex-1 overflow-y-auto p-4">
          <VersionHistoryPanel 
            blueprintId={blueprintId}
            onRestore={handleRestore}
            currentVersion={currentVersion}
          />
        </div>
      </div>
    );
  }

  // For Library/Glossary Items
  if (isLibraryRoute) {
    // Find the current glossary item to get its version
    const currentGlossaryItem = glossaryItems.find(item => item.id === glossaryItemId);
    
    // Extract current version
    let currentVersion: { major: number; minor: number; patch: number } | undefined;
    if (currentGlossaryItem?.version) {
      if ('major' in currentGlossaryItem.version) {
        currentVersion = {
          major: currentGlossaryItem.version.major,
          minor: currentGlossaryItem.version.minor,
          patch: currentGlossaryItem.version.patch,
        };
      }
    }

    const handleRestore = async () => {
      // Refresh the glossary data after restore
      await loadGlossaryData();
    };

    if (!glossaryItemId) {
      return (
        <div className="p-4 text-center text-muted-foreground">
          <p>{t('platform.layout.structure-menu.select_a_library_object_to_view_version_history')}</p>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full bg-background">
        <div className="flex-1 overflow-y-auto p-4">
          <GlossaryVersionHistoryPanel 
            glossaryId={glossaryItemId}
            onRestore={handleRestore}
            currentVersion={currentVersion}
          />
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <div className="p-4 text-center text-muted-foreground">
      <p>{t('platform.layout.structure-menu.no_version_history_available')}</p>
    </div>
  );
}